import { useMemo, useState } from "react";
import { DATA, ARCH_META } from "@/data/nations";

const mono = "'IBM Plex Mono',monospace";
const grot = "'Space Grotesk',sans-serif";
const sans = "'IBM Plex Sans',sans-serif";

const PRESETS = { Balanced: { SP: 25, EO: 20, BA: 20, BF: 20, OS: 15 } };
const FK = ["SP", "EO", "BA", "BF", "OS"];
const fColor = (v) => ["", "#C2613F", "#C99270", "#9FB1AE", "#4FA39C", "#0FA3A3"][v];
const fixedComposite = (c) => 5 * c.SP + 4 * c.EO + 4 * c.BA + 4 * c.BF + 3 * c.OS;
const tierOf = (s) => {
  if (s >= 70) return ["#0FA3A3", "Prime field", "top"];
  if (s >= 60) return ["#3E8ED0", "Strong", "high"];
  if (s >= 50) return ["#11476B", "Moderate", "mid"];
  if (s >= 40) return ["#8C97A1", "Challenging", "low"];
  return ["#C2613F", "Very hard", "bot"];
};
const inTier = (s, t) => {
  if (t === "top") return s >= 70;
  if (t === "high") return s >= 60 && s < 70;
  if (t === "mid") return s >= 50 && s < 60;
  if (t === "low") return s >= 40 && s < 50;
  if (t === "bot") return s < 40;
  return true;
};

const inputStyle = { background: "#13161C", border: "1px solid #2B2F37", color: "#F2F4F6", borderRadius: 8, padding: "9px 12px", fontFamily: sans, fontSize: 14 };
const btnStyle = { background: "#13161C", border: "1px solid #2B2F37", color: "#B6BFC9", borderRadius: 8, padding: "9px 13px", fontSize: 13, cursor: "pointer", fontFamily: sans };

const WEIGHT_LABELS = { SP: "Spiritual Priority", EO: "Economic Opportunity", BA: "Business Accessibility", BF: "BAM Sector Fit", OS: "Operating Stability" };
const TIERS = [
  ["All", "All tiers", "linear-gradient(90deg,#0FA3A3,#11476B,#C2613F)"],
  ["top", "Prime field (70+)", "#0FA3A3"],
  ["high", "Strong (60–69)", "#3E8ED0"],
  ["mid", "Moderate (50–59)", "#11476B"],
  ["low", "Challenging (40–49)", "#8C97A1"],
  ["bot", "Very hard (<40)", "#C2613F"],
];
const ARCH_ORDER = ["mega", "workshop", "platform", "wealth", "frontier"];

function NationCard({ r, i, sort }) {
  const [tc, tl] = tierOf(r.comp);
  return (
    <article data-testid={`nation-card-${r.n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{ background: "#13161C", border: "1px solid #23262D", borderRadius: 12, padding: "15px 16px 16px", display: "flex", flexDirection: "column", gap: 11, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: tc }} />
      <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
        <div style={{ fontFamily: mono, fontSize: 12, color: "#6B7680", minWidth: 30, paddingTop: 3 }}>{sort === "name" ? "•" : String(i + 1).padStart(2, "0")}</div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontFamily: grot, fontWeight: 600, fontSize: 17, letterSpacing: "-.01em", lineHeight: 1.15, margin: 0, color: "#F2F4F6" }}>{r.n}</h3>
          <span style={{ display: "inline-block", marginTop: 5, fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#6B7680", border: "1px solid #2B2F37", borderRadius: 5, padding: "2px 7px" }}>{r.r}</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 30, lineHeight: 1, color: tc }}>{r.comp}</div>
          <div style={{ fontSize: 9.5, letterSpacing: ".13em", textTransform: "uppercase", color: "#6B7680", marginTop: 3 }}>{tl}</div>
        </div>
      </div>
      <div style={{ height: 5, background: "#23262D", borderRadius: 3, overflow: "hidden" }}>
        <i style={{ display: "block", height: "100%", width: r.comp + "%", background: tc, borderRadius: 3, transition: "width .5s cubic-bezier(.16,1,.3,1)" }} />
      </div>
      <p style={{ fontSize: 12.5, color: "#9AA6B2", lineHeight: 1.45, margin: 0 }}>{r.d}</p>
      {r.f.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {r.f.map((f, j) => {
            const reached = /reached/.test(f);
            const warn = /conflict|fragile|closed|instab|disputed|crisis|frontier|security|stress|recovery/.test(f) && !reached;
            const sty = warn ? { color: "#C2613F", borderColor: "#5A3A2E", background: "rgba(194,97,63,.12)" } : { color: "#3E8ED0", borderColor: "#2E4A61", background: "rgba(62,142,208,.12)" };
            return <span key={j} style={{ fontFamily: mono, fontSize: 10, letterSpacing: ".05em", textTransform: "uppercase", padding: "2px 7px", borderRadius: 5, border: "1px solid", ...sty }}>{f}</span>;
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 1 }}>
        {FK.map((k) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontFamily: mono, fontSize: 10, color: "#6B7680", letterSpacing: ".04em" }}>{k}</span>
            <span style={{ width: 22, height: 22, borderRadius: 5, display: "grid", placeItems: "center", fontFamily: mono, fontSize: 12, fontWeight: 600, color: "#0c1019", background: fColor(r[k]) }}>{r[k]}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

export default function FieldIndex() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("composite");
  const [region, setRegion] = useState("All");
  const [tier, setTier] = useState("All");
  const [showW, setShowW] = useState(false);
  const [Wo, setWo] = useState(null);
  const W = Wo || PRESETS.Balanced;
  const tot = W.SP + W.EO + W.BA + W.BF + W.OS || 1;
  const pct = (k) => Math.round((W[k] / tot) * 100) + "%";

  const rows = useMemo(() => {
    let r = DATA.map((c) => ({ ...c, comp: Math.round(((c.SP * W.SP + c.EO * W.EO + c.BA * W.BA + c.BF * W.BF + c.OS * W.OS) / tot) * 20) }));
    if (region !== "All") r = r.filter((x) => x.r === region);
    if (tier !== "All") r = r.filter((x) => inTier(x.comp, tier));
    const qq = q.trim().toLowerCase();
    if (qq) r = r.filter((x) => x.n.toLowerCase().includes(qq) || x.r.toLowerCase().includes(qq) || x.d.toLowerCase().includes(qq));
    if (sort === "name") r.sort((a, b) => a.n.localeCompare(b.n));
    else if (sort === "composite") r.sort((a, b) => b.comp - a.comp || a.n.localeCompare(b.n));
    else r.sort((a, b) => b[sort] - a[sort] || b.comp - a.comp);
    return r;
  }, [q, sort, region, tier, W, tot]);

  const regions = useMemo(() => ["All", ...Array.from(new Set(DATA.map((d) => d.r)))], []);
  const tierName = { All: "", top: "Prime field", high: "Strong", mid: "Moderate", low: "Challenging", bot: "Very hard" }[tier];
  const countText = `${rows.length} of ${DATA.length} nations` + (region !== "All" ? " · " + region : "") + (tierName ? " · " + tierName : "") + (q.trim() ? ` · "${q.trim()}"` : "");

  return (
    <>
      {/* ARCHETYPES */}
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px 72px" }}>
        <div style={{ maxWidth: 760 }}>
          <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: ".2em", textTransform: "uppercase", color: "#0B7E7E", marginBottom: 14 }}>Five Archetypes</div>
          <h2 style={{ fontFamily: grot, fontWeight: 600, letterSpacing: "-.02em", fontSize: "clamp(26px,3.6vw,34px)", lineHeight: 1.1, margin: "0 0 16px", maxWidth: "24ch" }}>The nations don't reduce to a single ranking.</h2>
          <p style={{ fontSize: 16, color: "#3C4550", margin: "0 0 8px", lineHeight: 1.65 }}>They sort into five kinds of ground, and what you do in each is completely different.</p>
        </div>
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }} data-testid="archetype-cards">
          {ARCH_ORDER.map((key) => {
            const m = ARCH_META[key];
            const members = DATA.filter((c) => c.a === key).map((c) => ({ ...c, c2: fixedComposite(c) })).sort((a, b) => b.c2 - a.c2);
            return (
              <div key={key} style={{ background: "#fff", border: "1px solid #E2E6EA", borderLeft: "4px solid " + m.color, borderRadius: 14, padding: "20px 20px 18px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontFamily: grot, fontWeight: 600, fontSize: 21, letterSpacing: "-.01em" }}>{m.name}</h3>
                  <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: m.color, border: "1px solid " + m.color, borderRadius: 5, padding: "2px 8px" }}>{m.role}</span>
                  <span style={{ fontFamily: mono, fontSize: 12, color: "#8C97A1" }}>{members.length} nations</span>
                </div>
                <p style={{ fontSize: 14, color: "#3C4550", margin: "0 0 12px", lineHeight: 1.5 }}>{m.desc}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {members.map((c) => (
                    <span key={c.n} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fff", border: "1px solid #E2E6EA", borderRadius: 999, padding: "4px 10px 4px 9px", fontSize: 12, color: "#3C4550" }}>
                      {c.n.replace("United Arab Emirates", "UAE").replace("China, ", "").replace("Türkiye (Turkey)", "Türkiye")}
                      <b style={{ fontFamily: mono, fontSize: 11, color: m.color, fontWeight: 600 }}>{c.c2}</b>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* INTERACTIVE INDEX */}
      <div id="index" style={{ background: "#0C0E12", color: "#F2F4F6" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 24px 0" }}>
          <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: ".2em", textTransform: "uppercase", color: "#19C4C4", marginBottom: 12 }}>The Interactive Index</div>
          <h2 style={{ fontFamily: grot, fontWeight: 600, letterSpacing: "-.025em", fontSize: "clamp(28px,5vw,48px)", lineHeight: 1.02, margin: "0 0 14px", maxWidth: "20ch" }}>Test your own conviction <span style={{ color: "#19C4C4" }}>against the data.</span></h2>
          <p style={{ maxWidth: "66ch", color: "#9AA6B2", fontSize: 15.5, fontWeight: 300, lineHeight: 1.65, margin: 0 }}>The index is built to be argued with. Move the weighting toward spiritual priority and the hard frontiers climb. Weight economic opportunity and sector fit and the workshops rise. Same nations, different callings. Filter by region or score band to focus a conversation.</p>
        </div>

        <div style={{ position: "sticky", top: 62, zIndex: 30, marginTop: 26, background: "rgba(12,14,18,.88)", backdropFilter: "blur(10px)", borderTop: "1px solid #23262D", borderBottom: "1px solid #23262D" }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: "14px 24px" }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input data-testid="index-search-input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a country…" aria-label="Search countries" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
              <select data-testid="index-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by" style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="composite">Sort: Composite score</option>
                <option value="SP">Sort: Spiritual Priority</option>
                <option value="EO">Sort: Economic Opportunity</option>
                <option value="BA">Sort: Business Accessibility</option>
                <option value="BF">Sort: BAM Sector Fit</option>
                <option value="OS">Sort: Operating Stability</option>
                <option value="name">Sort: Name (A–Z)</option>
              </select>
              <button data-testid="adjust-weighting-btn" onClick={() => setShowW((s) => !s)} style={btnStyle}>Adjust weighting</button>
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 11 }} data-testid="region-pills">
              {regions.map((rg) => {
                const on = region === rg;
                return (
                  <button key={rg} data-testid={`region-pill-${rg.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setRegion(rg)} style={{ background: on ? "rgba(15,163,163,.16)" : "transparent", border: "1px solid " + (on ? "#0FA3A3" : "#2B2F37"), color: on ? "#19C4C4" : "#9AA6B2", borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", fontWeight: on ? 600 : 400, fontFamily: sans }}>{rg}</button>
                );
              })}
            </div>
            <div style={{ marginTop: 12, border: "1px solid #23262D", borderRadius: 10, background: "#13161C", padding: "14px 16px", display: showW ? "block" : "none" }} data-testid="weights-panel">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "14px 22px" }}>
                {FK.map((k) => (
                  <div key={k}>
                    <label style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#9AA6B2", marginBottom: 5 }}>
                      {WEIGHT_LABELS[k]} <b style={{ fontFamily: mono, color: "#19C4C4", fontWeight: 600 }}>{pct(k)}</b>
                    </label>
                    <input data-testid={`weight-slider-${k}`} type="range" min="0" max="60" value={W[k]} onChange={(e) => setWo({ ...W, [k]: +e.target.value })} aria-label={WEIGHT_LABELS[k] + " weight"} style={{ width: "100%", cursor: "pointer" }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, color: "#6B7680", flexWrap: "wrap", gap: 8 }}>
                <span>Weights normalize automatically — they need not sum to 100.</span>
                <button data-testid="reset-weights-btn" onClick={() => setWo(null)} style={{ ...btnStyle, padding: "7px 12px", fontSize: 12 }}>Reset to default</button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "16px 0 2px", alignItems: "center" }} data-testid="score-band-legend">
            <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "#6B7680", marginRight: 2, alignSelf: "center" }}>Score band</span>
            {TIERS.map((t) => {
              const on = tier === t[0];
              return (
                <button key={t[0]} data-testid={`tier-filter-${t[0].toLowerCase()}`} onClick={() => setTier(t[0])} style={{ display: "flex", alignItems: "center", gap: 7, background: on ? "rgba(15,163,163,.14)" : "transparent", border: "1px solid " + (on ? "#0FA3A3" : "#2B2F37"), color: on ? "#F2F4F6" : "#9AA6B2", borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", fontWeight: on ? 600 : 400, fontFamily: sans }}>
                  <i style={{ width: 11, height: 11, borderRadius: 3, display: "inline-block", flex: "none", background: t[2] }} />{t[1]}
                </button>
              );
            })}
          </div>
          <div style={{ fontFamily: mono, fontSize: 12, color: "#6B7680", margin: "14px 0 4px" }} data-testid="index-count-text">{countText}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(330px,1fr))", gap: 13, marginTop: 8 }} data-testid="nation-grid">
            {rows.map((r, i) => <NationCard key={r.n} r={r} i={i} sort={sort} />)}
          </div>
          <div style={{ marginTop: 40, borderTop: "1px solid #23262D", padding: "26px 0 56px" }}>
            <p style={{ fontSize: 14.5, color: "#9AA6B2", maxWidth: "80ch", lineHeight: 1.65, margin: "0 0 14px" }}><b style={{ color: "#F2F4F6", fontWeight: 600 }}>Two honest limits.</b> The scores are a directional 1–5 judgment combining current sector research with established country knowledge — a prioritization instrument, not real-time econometrics. And no national score can tell you the unit economics of a specific venture; that lives in a business plan and on the ground. <b style={{ color: "#F2F4F6", fontWeight: 600 }}>This map tells you where to look. It does not replace the partner who already lives there.</b></p>
            <p style={{ fontStyle: "italic", fontWeight: 300, fontSize: 15, color: "#9AA6B2", margin: "0 0 18px" }}>"The harvest is plentiful, but the laborers are few." — and increasingly, the laborers carry a ledger.</p>
            <p style={{ fontSize: 11.5, color: "#6B7680", margin: 0, lineHeight: 1.6 }}>Field figures from Gordon-Conwell's Center for the Study of Global Christianity; Window data from Joshua Project; Business-as-Mission framing from Mats Tunehag and the Lausanne Movement.</p>
          </div>
        </div>
      </div>
    </>
  );
}
